import React, { useState, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  styled,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Description as FileIcon,
} from "@mui/icons-material";
import config from "../../../config";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const DropZone = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isDragActive",
})<{ isDragActive?: boolean }>(({ theme, isDragActive }) => ({
  border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  textAlign: "center",
  backgroundColor: isDragActive
    ? theme.palette.action.hover
    : theme.palette.background.default,
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}));

interface ImportResult {
  imported: number;
  skipped: number;
  errors: { row: number; error: string }[];
}

interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  type:
    | "debtors"
    | "creditors"
    | "payments"
    | "receipts"
    | "vat-invoices"
    | "vat-payments"
    | "debtor-invoices"
    | "take-on-transactions";
  onImportComplete: () => void;
}

const ImportDialog: React.FC<ImportDialogProps> = ({
  open,
  onClose,
  type,
  onImportComplete,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && isValidFile(droppedFile)) {
      setFile(droppedFile);
      setResult(null);
      setError(null);
    } else {
      setError("Please upload a valid Excel (.xlsx, .xls) or CSV (.csv) file");
    }
  };

  const isValidFile = (f: File): boolean => {
    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "text/csv",
    ];
    const validExtensions = [".xlsx", ".xls", ".csv"];
    const extension = f.name.substring(f.name.lastIndexOf(".")).toLowerCase();
    return validTypes.includes(f.type) || validExtensions.includes(extension);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (isValidFile(selectedFile)) {
        setFile(selectedFile);
        setResult(null);
        setError(null);
      } else {
        setError("Please upload a valid Excel (.xlsx, .xls) or CSV (.csv) file");
      }
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem("vms_access_token");
      // Map UI types to backend types for receipts
      const backendType = type === "receipts" ? "receipts" : type;
      
      const response = await fetch(
        `${config.API_BASE_URL}/import/templates/${backendType}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${type}_import_template.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        setError("Failed to download template");
      }
    } catch (err) {
      setError("Error downloading template");
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem("vms_access_token");
      // Map UI types to backend types for receipts
      const backendType = type === "receipts" ? "receipts" : type;
      
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${config.API_BASE_URL}/import/${backendType}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const data: ImportResult = await response.json();
        setResult(data);
        if (data.imported > 0) {
          onImportComplete();
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error?.message || "Import failed");
      }
    } catch (err: any) {
      setError(err.message || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setError(null);
    onClose();
  };

  const getTypeLabel = () => {
    const labels: Record<string, string> = {
      debtors: "Debtors",
      creditors: "Creditors",
      payments: "Payments",
      receipts: "Receipts",
      "vat-invoices": "VAT Invoices",
      "vat-payments": "VAT Payments",
      "debtor-invoices": "Debtor Invoices",
      "take-on-transactions": "Take-On Transactions",
    };
    return labels[type] || type;
  };
  
  const typeLabel = getTypeLabel();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Import {typeLabel}</DialogTitle>
      <DialogContent>
        {/* Download Template Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Step 1: Download Template
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Use our template to ensure your data is formatted correctly.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
            size="small"
          >
            Download {typeLabel} Template
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Upload Section */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Step 2: Upload Your File
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload a completed Excel (.xlsx, .xls) or CSV (.csv) file.
          </Typography>

          <DropZone
            isDragActive={isDragActive}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <CloudUploadIcon
              sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
            />
            <Typography variant="body1" gutterBottom>
              {file ? file.name : "Drag & drop your file here"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {file
                ? `${(file.size / 1024).toFixed(1)} KB`
                : "or click to browse"}
            </Typography>
            <VisuallyHiddenInput
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
            />
          </DropZone>

          {file && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                bgcolor: "action.hover",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <FileIcon color="primary" />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {file.name}
              </Typography>
              <Button
                size="small"
                color="error"
                onClick={() => setFile(null)}
              >
                Remove
              </Button>
            </Box>
          )}
        </Box>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Results Display */}
        {result && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Import Results
            </Typography>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  bgcolor: "success.light",
                  borderRadius: 1,
                  textAlign: "center",
                }}
              >
                <Typography variant="h4" color="success.dark">
                  {result.imported}
                </Typography>
                <Typography variant="body2" color="success.dark">
                  Imported
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  bgcolor: "warning.light",
                  borderRadius: 1,
                  textAlign: "center",
                }}
              >
                <Typography variant="h4" color="warning.dark">
                  {result.skipped}
                </Typography>
                <Typography variant="body2" color="warning.dark">
                  Skipped
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  bgcolor: "error.light",
                  borderRadius: 1,
                  textAlign: "center",
                }}
              >
                <Typography variant="h4" color="error.dark">
                  {result.errors.length}
                </Typography>
                <Typography variant="body2" color="error.dark">
                  Errors
                </Typography>
              </Box>
            </Box>

            {result.errors.length > 0 && (
              <Box>
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                  Issues found:
                </Typography>
                <List dense sx={{ maxHeight: 200, overflow: "auto" }}>
                  {result.errors.slice(0, 10).map((err, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <WarningIcon color="warning" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`Row ${err.row}: ${err.error}`}
                        primaryTypographyProps={{ variant: "body2" }}
                      />
                    </ListItem>
                  ))}
                  {result.errors.length > 10 && (
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={`... and ${result.errors.length - 10} more errors`}
                        primaryTypographyProps={{
                          variant: "body2",
                          color: "text.secondary",
                        }}
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {result ? "Close" : "Cancel"}
        </Button>
        {!result && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!file || loading}
            startIcon={
              loading ? <CircularProgress size={20} /> : <CloudUploadIcon />
            }
          >
            {loading ? "Importing..." : "Import"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportDialog;
